import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { GlobalFocusToggle } from '@els/els-react--utils';
import HeaderComponentContainer from '@Containers/Header/HeaderComponentContainer';
import Footer from '@Components/Footer';
import ContentClickManager from '@Components/ContentClickManager';

import './Page.scss';

export default function Page({
  id,
  withSubTabs = false,
  withHeader = false,
  withFooter = true,
  renderHeaderInsideScrollRoot = false,
  headerType,
  pageClassName,
  className,
  children,
}) {
  // The header renders as a sibling ABOVE `.react-page` for the classic opt-in (`withHeader`),
  // but INSIDE the `.react-page` scroll root when `renderHeaderInsideScrollRoot` is set so it
  // scrolls away with the page content (FR-1.1). The caller must ensure the app-level global
  // header (Main.jsx) is suppressed for this route to avoid a double header.
  //
  // A11y note: `.react-page` is a plain `<div>`, not a `<section>`. The Leyden `<Header>` renders
  // a bare `<header>` with no explicit `role` (it doesn't forward one), and per the HTML-AAM spec
  // a `<header>` only maps to the `banner` landmark when it is NOT a descendant of `<article>`,
  // `<aside>`, `<main>`, `<nav>`, or `<section>`. `.react-page` carries no accessible name, so it
  // was never a `region` landmark itself — using `<section>` only cost the nested header its
  // `banner` role for no semantic benefit. Keep this a `<div>` so `banner` is preserved when the
  // header renders inside (see Page.test.jsx a11y regression test).
  return (
    <ContentClickManager>
      <GlobalFocusToggle>
        {withHeader && !renderHeaderInsideScrollRoot && <HeaderComponentContainer type={headerType} />}
        <div
          id={id}
          className={classnames(pageClassName, 'react-page', {
            // FR-1.1 / AR-1.3: a shared styling hook so covered routes (header rendered inside the
            // scroll root) can revalidate the header-era `.react-page` min/max-height without
            // touching unchanged routes (Page.scss).
            'react-page--header-inside': renderHeaderInsideScrollRoot,
          })}>
          {renderHeaderInsideScrollRoot && <HeaderComponentContainer type={headerType} />}
          <section
            className={classnames('react-page--children-wrapper', className, {
              'react-page--sub-tabs-padding': withSubTabs,
            })}>
            {children}
          </section>
          {withFooter && <Footer className="react-page-footer" />}
        </div>
      </GlobalFocusToggle>
    </ContentClickManager>
  );
}

Page.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  withHeader: PropTypes.bool,
  renderHeaderInsideScrollRoot: PropTypes.bool,
  headerType: PropTypes.string,
  withFooter: PropTypes.bool,
  withSubTabs: PropTypes.bool,
  pageClassName: PropTypes.string,
  children: PropTypes.node,
};
